import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { ShippingProfileApi } from "@/api/shippingProfile.api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const CONTINENTS = [
  { code: "NORTH_AMERICA", name: "North America" },
  { code: "SOUTH_AMERICA", name: "South America" },
  { code: "AUSTRALIA", name: "Australia" },
  { code: "EUROPE", name: "Europe" },
  { code: "ASIA", name: "Asia" },
  { code: "AFRICA", name: "Africa" },
  { code: "ANTARCTICA", name: "Antarctica" },
];

const COUNTRIES_BY_CONTINENT = {
  AUSTRALIA: [
    "Australia", "Fiji", "Kiribati", "Marshall Islands", "Micronesia", "Nauru", "New Zealand", "Palau", "Papua New Guinea", "Samoa", "Solomon Islands", "Tonga", "Tuvalu", "Vanuatu"
  ],
  NORTH_AMERICA: [
    "United States", "Canada", "Mexico", "Greenland", "Bermuda", "Saint Pierre and Miquelon"
  ],
  SOUTH_AMERICA: [
    "Brazil", "Argentina", "Colombia", "Chile", "Peru", "Venezuela", "Ecuador", "Bolivia", "Paraguay", "Uruguay", "Guyana", "Suriname"
  ],
  EUROPE: [
    "United Kingdom", "France", "Germany", "Italy", "Spain", "Netherlands", "Belgium", "Switzerland", "Austria", "Sweden", "Norway", "Denmark", "Finland", "Ireland", "Portugal"
  ],
  ASIA: [
    "China", "India", "Japan", "South Korea", "Indonesia", "Pakistan", "Bangladesh", "Vietnam", "Philippines", "Thailand", "Malaysia", "Singapore", "Nepal", "Sri Lanka"
  ],
  AFRICA: [
    "Nigeria", "Ethiopia", "Egypt", "South Africa", "Kenya", "Uganda", "Algeria", "Sudan", "Morocco", "Ghana", "Mozambique", "Angola", "Ivory Coast", "Madagascar"
  ],
  ANTARCTICA: [
    "Research Station 1", "Research Station 2", "Research Station 3"
  ]
};

export default function CreateShippingProfile() {
  const [name, setName] = useState("");
  const [selectedContinents, setSelectedContinents] = useState<string[]>(["AUSTRALIA"]);
  const [selectedContinent, setSelectedContinent] = useState("AUSTRALIA");
  const [selectedCountries, setSelectedCountries] = useState<string[]>(["Marshall Islands", "New Zealand", "Samoa"]);
  const [countrySearch, setCountrySearch] = useState("");
  const [allCountries, setAllCountries] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchCountries() {
      try {
        const { data } = await ShippingProfileApi.fetchAllCountries();
        setAllCountries(data || []);
      } catch {
        setAllCountries([]);
      }
    }
    fetchCountries();
  }, []);

  const countries = COUNTRIES_BY_CONTINENT[selectedContinent] || [];
  const filteredCountries = countries.filter(c => c.toLowerCase().includes(countrySearch.toLowerCase()));

  const handleContinentToggle = (code: string) => {
    setSelectedContinents(prev => {
      let updated;
      if (prev.includes(code)) {
        updated = prev.filter(c => c !== code);
      } else {
        updated = [...prev, code];
      }
      // If the toggled continent is being selected, set as selectedContinent
      if (!prev.includes(code)) {
        setSelectedContinent(code);
        // When switching, only keep selected countries that are in the new continent
        setSelectedCountries(countries => (COUNTRIES_BY_CONTINENT[code] || []).filter(c => countries.includes(c)));
      } else if (selectedContinent === code) {
        // If the toggled continent is being deselected and was selectedContinent, pick another or clear
        const newCont = updated[0] || "";
        setSelectedContinent(newCont);
        setSelectedCountries(countries => (COUNTRIES_BY_CONTINENT[newCont] || []).filter(c => countries.includes(c)));
      }
      return updated;
    });
  };

  const handleCountryToggle = (country: string) => {
    setSelectedCountries(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    );
  };

  const handleSelectAllCountries = () => {
    setSelectedCountries(filteredCountries);
  };
  const handleClearAllCountries = () => {
    setSelectedCountries([]);
  };
  const handleResetCountries = () => {
    setCountrySearch("");
    setSelectedCountries([]);
  };
  const handleClearAllContinents = () => {
    setSelectedContinents([]);
    setSelectedCountries([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (selectedCountries.length === 0) {
      toast.error("Select at least one country");
      return;
    }
    // Map selectedCountries to [{id, continent}]
    const countryPayload = selectedCountries.map(countryName => {
      const countryObj = allCountries.find(c => c.name === countryName);
      return countryObj ? { id: countryObj.id, continent: countryObj.continent } : null;
    }).filter(Boolean);
    if (countryPayload.length === 0) {
      toast.error("Selected countries are invalid");
      return;
    }
    try {
      await ShippingProfileApi.createOrUpdateShippingProfile({
        name,
        description: "",
        countries: countryPayload
      });
      toast.success("Shipping profile created successfully");
      navigate("/rewardpoint/shipping-profiles");
    } catch (err) {
      toast.error(err?.message || "Failed to create shipping profile");
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/shipping-profiles">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Create Shipping Profile</h1>
          {/* <p className="text-gray-500">Create shipping profile</p> */}
        </div>
      </div>
      <form className="flex flex-col md:flex-row gap-8" onSubmit={handleSubmit}>
        {/* Left: Name & Continents */}
        <div className="flex-1 min-w-[320px]">
          <label className="block font-medium mb-2">Name*</label>
          <Input
            placeholder="Enter shipping profile name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="mb-6"
          />
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">Continents</span>
            <Button variant="link" type="button" className="p-0 h-auto text-xs" onClick={handleClearAllContinents}>Clear All</Button>
          </div>
          <div className="flex flex-col gap-3">
            {CONTINENTS.map(cont => (
              <div
                key={cont.code}
                className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-colors ${selectedContinents.includes(cont.code) ? "bg-gray-100 border-gray-300" : "bg-white border-gray-200"}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedContinents.includes(cont.code)}
                    onCheckedChange={() => handleContinentToggle(cont.code)}
                  />
                  <span className="font-medium text-sm">{cont.code}</span>
                </div>
                <Button variant="ghost" size="icon" className="ml-2">
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Button>
              </div>
            ))}
          </div>
        </div>
        {/* Right: Countries for selected continent */}
        <div className="flex-1 min-w-[320px]">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-bold text-lg uppercase tracking-wide">{selectedContinent}</span>
              <div className="flex gap-2 text-xs">
                <Button variant="link" type="button" className="p-0 h-auto" onClick={handleSelectAllCountries}>Select All</Button>
                <Button variant="link" type="button" className="p-0 h-auto" onClick={handleClearAllCountries}>Clear All</Button>
                <Button variant="link" type="button" className="p-0 h-auto" onClick={handleResetCountries}>Reset</Button>
              </div>
            </div>
            <div className="flex items-center mb-4">
              <Input
                placeholder="Search Country"
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
                className="flex-1"
              />
              <Button variant="ghost" size="icon" className="ml-2">
                <Search className="w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredCountries.map(country => (
                <label key={country} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedCountries.includes(country)}
                    onCheckedChange={() => handleCountryToggle(country)}
                  />
                  <span>{country}</span>
                </label>
              ))}
              {filteredCountries.length === 0 && (
                <span className="col-span-full text-gray-400 text-sm">No countries found.</span>
              )}
            </div>
          </Card>
        </div>
      </form>
      <div className="flex justify-end gap-4 mt-8">
        <Button variant="outline" type="button" onClick={() => { setName(""); setSelectedContinents([]); setSelectedCountries([]); setCountrySearch(""); }}>Reset</Button>
        <Button className="bg-black text-white hover:bg-gray-900" type="submit" onClick={handleSubmit}>Submit</Button>
      </div>
    </div>
  );
} 